import type { PlayMode } from "@volley/shared/apps/app-common-models";

import {
    LACROSSE_COURT_DIMENSIONS,
    LACROSSE_DIAGRAM,
} from "../../../util/lacrosse/diagram";
import type { ShotSpread } from "../../../util/lacrosse/shotSpread";
import type { CoordSys } from "../../../util/position-types";
import { Sport } from "../../common/context/sport";

import { CourtDimensions, CourtSize } from "./types";

const padelCourtImage = new URL(
    "../../../static/img/padel-full-court.png",
    import.meta.url,
).toString();
const padelHalfCourtImage = new URL(
    "../../../static/img/padel-half-court.png",
    import.meta.url,
).toString();
const platformCourtImage = new URL(
    "../../../static/img/platform-full-court.png",
    import.meta.url,
).toString();
const platformHalfCourtImage = new URL(
    "../../../static/img/platform-half-court.png",
    import.meta.url,
).toString();
const tennisCourtImage = new URL(
    "../../../static/img/tennis-full-court.png",
    import.meta.url,
).toString();
const tennisHalfCourtImage = new URL(
    "../../../static/img/tennis-half-court.png",
    import.meta.url,
).toString();

const pickleballCourtImage = new URL(
    "../../../static/models/textures/pickeball-court.jpg",
    import.meta.url,
).toString();
const pickleballHalfCourtImage = new URL(
    "../../../static/models/textures/pickleball-court-half.jpg",
    import.meta.url,
).toString();

export const CourtImages: Record<CourtSize, Record<Sport, string>> = {
    FULL: {
        PADEL: padelCourtImage as string,
        PLATFORM_TENNIS: platformCourtImage as string,
        TENNIS: tennisCourtImage as string,
        PICKLEBALL: pickleballCourtImage,
        /*
            An inline SVG data URL rather than a PNG, drawn from the same
            rulebook table the 3D field is. One diagram serves all three
            variants - see util/lacrosse/diagram.ts. FULL and HALF are the same
            image because lacrosse has no meaningful half-field.
        */
        LACROSSE: LACROSSE_DIAGRAM,
    },
    HALF: {
        PADEL: padelHalfCourtImage as string,
        PLATFORM_TENNIS: platformHalfCourtImage as string,
        TENNIS: tennisHalfCourtImage as string,
        PICKLEBALL: pickleballHalfCourtImage,
        LACROSSE: LACROSSE_DIAGRAM,
    },
};

export const IconDimensions = {
    trainerMarkerWidth: 48,
    trainerMarkerHeight: 48,
    trainerRingWidth: 66,
    trainerRingHeight: 98,
    trainerSolidWidth: 48,
    trainerSolidHeight: 72,
    circleMarkerWidth: 36,
    circleMarkerHeight: 36,
};

export const CourtTable: Record<Sport, CourtDimensions> = {
    PADEL: {
        widthMeters: 10.2,
        heightMeters: {
            FULL: 20.2,
            HALF: 10.62,
        },
        width: 400,
        height: {
            FULL: 778,
            HALF: 409,
        },
        xOffset: 200,
        yOffset: {
            FULL: 389,
            HALF: 5,
        },
    },
    PICKLEBALL: {
        widthMeters: 7.92,
        heightMeters: {
            FULL: 16.46,
            HALF: 8.23,
        },
        width: 482,
        height: {
            FULL: 1000,
            HALF: 500,
        },
        xOffset: 240,
        yOffset: {
            FULL: 500,
            HALF: -50,
        },
    },
    PLATFORM_TENNIS: {
        widthMeters: 9.144,
        heightMeters: {
            FULL: 18.288,
            HALF: 11.6,
        },
        width: 400,
        height: {
            FULL: 800,
            HALF: 507,
        },
        xOffset: 200,
        yOffset: {
            FULL: 400,
            HALF: 107,
        },
    },
    TENNIS: {
        widthMeters: 18.3,
        heightMeters: {
            FULL: 36.58,
            HALF: 24.7,
        },
        width: 400,
        height: {
            FULL: 800,
            HALF: 540,
        },
        xOffset: 200,
        yOffset: {
            FULL: 400,
            HALF: 140,
        },
    },

    /*
        A 40 x 40 m square centred on the goal, at 12.5 px/m. Not a stretched
        tennis frame: tennis is 18.3 m wide and the lacrosse spots reach
        +/-15.9, so WIDTH is the binding constraint, not length.

        Square pixels and `yOffset === height / 2` are both load-bearing - see
        LACROSSE_COURT_DIMENSIONS, which is where they are explained and
        asserted.
    */
    LACROSSE: LACROSSE_COURT_DIMENSIONS,
};

/**
 * How far the yaw slider may swing the machine OFF the aim its spot declares,
 * in degrees.
 *
 * An offset, not an absolute body angle. The slider used to be absolute, whose
 * zero is "facing physics +y" - a direction with nothing to do with where the
 * machine is standing. So a lacrosse spot on the left of the cage read +55 and
 * its mirror on the right read -55 for what a coach would call the same thing,
 * and the six spots that face down the field sat at +/-180, hard against an
 * end of the range and unable to swing one way at all.
 *
 * Measuring from the spot's own aim fixes all of that: every spot starts
 * centred at 0 with equal room either side, and the number means what a coach
 * would say out loud - "20 degrees off it".
 *
 * Pickleball keeps +/-75. Its reference is 0, because its spots declare no
 * yaw, so offset and absolute are the same thing there and nothing changes.
 */
export function yawOffsetRangeForSport(sport: Sport): number {
    return sport === "LACROSSE" ? 90 : 75;
}

/**
 * Whether a sport offers manual yaw adjustment on top of its spot's own yaw.
 *
 * Pickleball has had it for a while. Lacrosse needs it more: its spots are
 * aimed - at the cage, or down the field - and a coach running a drill off to
 * one side wants to nudge that without moving the machine.
 */
export function yawControlAvailable(sport: Sport): boolean {
    return sport === "PICKLEBALL" || sport === "LACROSSE";
}

/**
 * Marks for the yaw slider, out to the sport's offset limit.
 *
 * Stepped outward from 0 rather than up from -limit, so the centre - the
 * spot's own aim - is always a mark. The step divides the limit exactly: 30 for
 * lacrosse's 90, 25 for the 75 pickleball already had.
 */
export function yawSliderMarks(
    sport: Sport,
): { label: string; value: number }[] {
    const limit = yawOffsetRangeForSport(sport);
    const step = limit % 30 === 0 ? 30 : 25;
    const centre = sport === "LACROSSE" ? "Aimed" : "Forward";
    const values = [0];
    for (let i = step; i < limit; i += step) values.push(i, -i);
    values.push(limit, -limit);
    return values
        .sort((a, b) => a - b)
        .map((value) => ({
            label: value === 0 ? centre : `${value}°`,
            value,
        }));
}

/**
 * Whether the picker draws the machine's pan cone for this sport.
 *
 * Lacrosse only for now. The cone answers "how much of the field can I cover
 * from this spot without moving", which matters most where the spots ring a
 * cage and each one is aimed - on a court the machine is behind a baseline
 * facing across the net and the answer is much less interesting.
 *
 * Nothing about it is lacrosse-specific though: it reads the machine's own
 * reported pan range, so widening it to another sport is a change to this line
 * and nothing else.
 */
export function panConeAvailable(sport: Sport): boolean {
    return sport === "LACROSSE";
}

/**
 * Which frame a sport's stored coordinates are in.
 *
 * Platform tennis is the one sport whose positions are court-relative;
 * everything else is physics. Named rather than written inline, because the
 * draw path used to re-derive it from the same one input at six separate
 * sites.
 */
export function coordSysForSport(sport: Sport): CoordSys {
    return sport === "PLATFORM_TENNIS" ? "court" : "physics";
}

/**
 * Apps whose drills must never alternate sides, whatever the setup.
 *
 * 15, AOI Triggered, and 19, its lacrosse fork, are here because the drill is
 * built around a trigger zone the player stands in: the shots answer where
 * that player IS, so replaying each one at the mirrored pan aims the answer at
 * a zone nobody is standing in.
 * The app has always hidden the "both" button for that reason; this stops the
 * lacrosse two-cage setup forcing the mode back on behind its back.
 */
const NO_DUAL_APP_IDS: readonly number[] = [15, 19];

/** May this app's drill be played as alternating sides? */
export function appSupportsDual(appId?: number): boolean {
    return appId === undefined || !NO_DUAL_APP_IDS.includes(appId);
}

/**
 * The play mode a drill FORCES, or null where the coach picks one.
 *
 * Lacrosse is the only forcing sport, and it forces something in three of its
 * four cases. The deciding fact is the drill's own shots: "dual" plays each
 * shot and then plays it again with the pan negated, so it is only worth
 * anything when every angled shot leans the SAME way. A drill that already
 * works both sides has no mirror image - negating its pans swaps left for
 * right and hands back the same drill.
 *
 *   two cages, one-sided  -> "dual".   Alternating between the cages is not one
 *                                      way to run this drill, it is what the
 *                                      drill IS: two cages straddle the
 *                                      machine's aim and it feeds them turn and
 *                                      turn about. The negated pan is exactly
 *                                      the second cage, because the machine
 *                                      stands on the line bisecting the pair
 *                                      and faces square down it.
 *   one cage,  one-sided  -> null.     There is a real second side to feed, so
 *                                      the coach chooses.
 *   anything else         -> "standard". Nothing to mirror. Forcing the mode
 *                                      rather than returning null is also what
 *                                      keeps the deuce/ad picker off a lacrosse
 *                                      screen: a forced mode suppresses it, and
 *                                      service courts do not exist on a field.
 *
 * `appId` opts a drill out of alternating entirely - see NO_DUAL_APP_IDS. Absent
 * means it may alternate, which is right for every app but the listed ones.
 *
 * Nothing new was needed on the trainer for any of this - coach has understood
 * all three modes since long before lacrosse.
 */
export function forcedPlayMode(
    sport: Sport,
    lacrosseTwoCage: boolean,
    spread: ShotSpread,
    appId?: number,
): PlayMode | null {
    if (sport !== "LACROSSE") return null;
    if (!appSupportsDual(appId)) return "standard";
    if (spread !== "one-sided") return "standard";
    return lacrosseTwoCage ? "dual" : null;
}

/**
 * The mode a drill will actually play, given the one the screen is holding.
 *
 * Two things can override a coach's request. A drill may FORCE a mode - see
 * forcedPlayMode - and lacrosse may not play "mirror" at all.
 *
 * "mirror" is the one mode that moves the machine: useAppWorkoutPlay swaps the
 * planned position for mirrorTrainer's, which negates x AND yaw. The racket
 * sports get away with that because they have no yaw control at all (see
 * yawControlAvailable) - their machine always faces forward, so mirroring
 * slides it across the centreline and the deuce/ad workflow is exactly that
 * move. Lacrosse DOES have yaw control and its spots are angled at the cage, so
 * the same reflection rotates the machine as well as moving it, and the side
 * control would be asking a coach to go and re-place the trainer mid-drill.
 *
 * These controls only ever choose the pan of shots. "dual" honours that - it
 * leaves the machine where it stands and alternates the sign - so lacrosse
 * offers that and standard, and a stored "mirror" degrades to standard rather
 * than quietly relocating the machine.
 */
export function effectivePlayMode(
    requested: PlayMode,
    sport: Sport,
    lacrosseTwoCage: boolean,
    spread: ShotSpread,
    appId?: number,
): PlayMode {
    const forced = forcedPlayMode(sport, lacrosseTwoCage, spread, appId);
    if (forced !== null) return forced;
    if (sport === "LACROSSE" && requested === "mirror") return "standard";
    return requested;
}
